import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { useDemoStore } from '@/store/demoStore';
import { web3Service } from '@/services/web3';
import { 
  Package, 
  Upload, 
  AlertCircle,
  Info,
  Calendar,
  DollarSign,
  Lock,
  Layers
} from 'lucide-react';
import useApiService from '@/services/apiService';

export default function CreateDropPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isDemoMode } = useDemoStore();
  const apiService = useApiService();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // File object for upload - used in handleCoverChange
  const [coverFile, setCoverFile] = useState<File | null>(null);
  console.log('Cover file ready for upload:', coverFile?.name);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [initialSupply, setInitialSupply] = useState(10);
  const [maxSupply, setMaxSupply] = useState(100);
  const [royaltyPercentage, setRoyaltyPercentage] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTokenGated, setIsTokenGated] = useState(false);
  const [tokenAmount, setTokenAmount] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dropId, setDropId] = useState('');
  const [step, setStep] = useState(1);

  // Handle cover file selection
  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Create drop
  const handleCreateDrop = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a drop title');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      if (isDemoMode) {
        // In demo mode, simulate drop creation
        setTimeout(() => {
          toast.success('Drop created successfully!');
          setDropId('demo-drop-' + Math.floor(Math.random() * 1000));
          setStep(2);
          setIsLoading(false);
        }, 1500);
      } else {
        // In real mode, create drop via API and blockchain
        // 1. Upload metadata to IPFS
        const metadataUri = await uploadMetadataToIPFS();
        
        // 2. Create drop on blockchain
        const dropId = await web3Service.createDrop(
          user?.walletAddress || '',
          initialSupply,
          maxSupply,
          metadataUri,
          royaltyPercentage
        );
        
        // 3. Save drop details to backend
        const dropData = {
          title,
          description,
          contractAddress: web3Service.getDropContractAddress(),
          tokenId: dropId,
          initialSupply,
          maxSupply,
          royaltyPercentage,
          startDate: startDate || new Date().toISOString(),
          endDate: endDate || null,
          isTokenGated,
          tokenAmount: isTokenGated ? tokenAmount : 0,
          coverImage: coverPreview || '',
        };
        
        await apiService.createDrop(dropData);
        
        toast.success('Drop created successfully!');
        setDropId(dropId);
        setStep(2);
      }
    } catch (error) {
      console.error('Error creating drop:', error);
      toast.error('Failed to create drop');
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Upload metadata to IPFS (mock implementation)
  const uploadMetadataToIPFS = async () => {
    // In a real implementation, this would upload to IPFS
    // For now, we'll just return a mock URI
    return 'ipfs://QmXyZ123456789abcdef123456789abcdef123456';
  };

  // Go to drops dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard/drops');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-8">
        <Package className="h-6 w-6 mr-2 text-primary" />
        <h1 className="text-3xl font-bold text-white">Create New Drop</h1>
      </div>
      
      {step === 1 ? (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <form onSubmit={handleCreateDrop}>
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-red-200">{error}</p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Drop Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                placeholder="Enter a title for your drop"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none min-h-[120px]"
                placeholder="Describe your drop (optional)"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Cover Image</label>
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="relative border-2 border-dashed border-dark-600 rounded-lg p-4 hover:border-primary transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center py-4">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-400">Click or drag to upload cover image</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG or GIF (max 2MB)</p>
                    </div>
                  </div>
                </div>
                
                {coverPreview && (
                  <div className="w-1/3">
                    <div className="aspect-square rounded-lg overflow-hidden bg-dark-700">
                      <img 
                        src={coverPreview} 
                        alt="Cover preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-medium mb-2">Initial Supply</label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={initialSupply}
                  onChange={(e) => setInitialSupply(parseInt(e.target.value))}
                  className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Number of NFTs to mint initially</p>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Maximum Supply</label>
                <input
                  type="number"
                  min={initialSupply}
                  max="100000"
                  value={maxSupply}
                  onChange={(e) => setMaxSupply(parseInt(e.target.value))}
                  className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum number of NFTs that can be minted</p>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Royalty Percentage</label>
              <input
                type="range"
                min="0"
                max="20"
                step="1"
                value={royaltyPercentage}
                onChange={(e) => setRoyaltyPercentage(parseInt(e.target.value))}
                className="w-full h-2 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between mt-2">
                <span className="text-sm text-gray-400">0%</span>
                <span className="text-sm text-primary font-medium">{royaltyPercentage}%</span>
                <span className="text-sm text-gray-400">20%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Percentage of secondary sales you'll receive as royalties</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white font-medium mb-2">Start Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full bg-dark-700 text-white rounded-lg p-3 pl-10 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">When the drop becomes available (optional)</p>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">End Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full bg-dark-700 text-white rounded-lg p-3 pl-10 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">When the drop ends (optional)</p>
              </div>
            </div>
            
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="token-gated"
                  checked={isTokenGated}
                  onChange={(e) => setIsTokenGated(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-600 text-primary focus:ring-primary"
                />
                <label htmlFor="token-gated" className="ml-2 text-white font-medium flex items-center">
                  <Lock className="h-4 w-4 mr-1 text-primary" />
                  Enable Token Gating
                </label>
              </div>
              
              {isTokenGated && (
                <div className="bg-dark-700 p-4 rounded-lg border border-dark-600 ml-6">
                  <div className="flex items-center mb-4">
                    <Layers className="h-5 w-5 text-primary mr-2" />
                    <h3 className="text-white font-medium">FTTF Token Requirement</h3>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-2">Required Token Amount</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="number"
                        min="1"
                        value={tokenAmount}
                        onChange={(e) => setTokenAmount(parseInt(e.target.value))}
                        className="w-full bg-dark-900 text-white rounded-lg p-3 pl-10 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Users need this many FTTF tokens to access this drop</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="mr-4"
                onClick={() => navigate('/dashboard/drops')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <span>Create Drop</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <div className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-lg flex items-start">
            <Info className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-200 font-medium">Drop created successfully!</p>
              <p className="text-green-300 text-sm mt-1">Your drop is now ready. You can start minting NFTs for this collection.</p>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Drop Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Drop Title</label>
                <p className="text-white font-medium">{title}</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Supply</label>
                <p className="text-white font-medium">{initialSupply} / {maxSupply}</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Royalty</label>
                <p className="text-white font-medium">{royaltyPercentage}%</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Token Gating</label>
                <p className="text-white font-medium flex items-center">
                  {isTokenGated ? (
                    <>
                      <Lock className="h-4 w-4 mr-1 text-primary" />
                      Enabled ({tokenAmount} FTTF)
                    </>
                  ) : (
                    'Disabled'
                  )}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Drop ID</h2>
            
            <div className="bg-dark-700 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-400 text-sm">Collection ID</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(dropId);
                    toast.success('Drop ID copied to clipboard');
                  }}
                  className="text-xs"
                >
                  Copy
                </Button>
              </div>
              <div className="bg-dark-900 p-3 rounded border border-dark-600 font-mono text-primary">
                {dropId}
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/nfts/mint')}
            >
              Mint NFTs for this Drop
            </Button>
            <Button
              onClick={handleGoToDashboard}
              className="bg-primary hover:bg-primary/90"
            >
              Go to Drops Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
